import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { HttpService, MessageService } from 'src/app/core';
import { ConstantConfig } from 'src/app/core/config/constant.config';
import { FILTER_TYPES } from 'src/app/core/config/enum';
import { ExcelTypeFilterService } from 'src/app/core/services/excel-type-filter.service';
import { Utility } from 'src/app/core/utils/utility';
import { LSSearchInputComponent } from '../search-input/search-input.component';

@Component({
  selector: 'app-excel-type-filter',
  templateUrl: './excel-type-filter.component.html',
  styleUrls: ['./excel-type-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ExcelTypeFilterComponent {
  fullList: any[] = []; // unfiltered full list
  filteredList: any[] = [];

  @Output() cancelClicked = new EventEmitter<void>();
  @Input() options: any = [];
  @Output() resetClicked = new EventEmitter<any>();
  @Input() savedFilterCloned: any = {};

  DATE_FORMAT_MMDDYYYY = ConstantConfig.DATE_FORMAT_MMDDYYYY;
  columnConfig: any = {};
  apiEndpoint: string = '';
  isScrolled: boolean = false;
  currentPage: number = 1;
  totalPage: number = 1;
  searchText: string = '';
  savedFilterClonedBackup: any = {};
  isSearchActive: boolean = false;
  isSelectAll: boolean = true;
  isSelectAllSearchedResult: boolean = true;
  @ViewChild(LSSearchInputComponent) searchComponent!: LSSearchInputComponent;
  searchedResults: any[] = [];
  mergedList: any[] = [];
  previousSearchTerm: string = '';
  FILTER_TYPES = FILTER_TYPES;
  US_PHONE_MASKING = ConstantConfig.US_PHONE_MASKING;

  constructor(
    private httpService: HttpService,
    private msgService: MessageService,
    private excelTypeFilterService: ExcelTypeFilterService
  ) { }

  ngOnInit() {
    this.savedFilterClonedBackup = { ...this.savedFilterCloned };
    this.options.filter_search = this.searchText;
    this.columnConfig = this.excelTypeFilterService.getColumnConfig(this.options.tableName, this.options.columnName);
    this.apiEndpoint = this.excelTypeFilterService.getApiEndpoint(this.options.tableName, this.options.historicData ? 1 : 0);

    const urlPaymentId = this.getUrlPaymentId();
    if (urlPaymentId) {
      this.searchText = urlPaymentId;
      this.isSearchActive = true;
      this.previousSearchTerm = urlPaymentId.toLowerCase();
      this.options.filter_search = urlPaymentId.toLowerCase();
    }

    if (this.apiEndpoint) {
      this.options.filterObject = {
        ...this.options.filterObject,
        [this.columnConfig?.uniqueFilter?.paramName[0]]: 1
      };
      this.options.filterObject.page = 1;
      this.options.filterObject.filter_limit = this.getFilterLimit();
      if (urlPaymentId) {
        this.options.filterObject.filter_search = urlPaymentId.toLowerCase();
      }
      this.getColumnUniqueValues();
    }
  }

  initializeList(data: any) {
    let selectedValues: string[] = [];

    if (this.options?.filterObject?.selectedValues && this.options?.filterObject?.selectedValues[this.options?.filterObject?.paramName]?.length) {
      selectedValues = this.options?.filterObject?.selectedValues[this.options?.filterObject?.paramName][0]?.split(',') || [];
    }

    const urlPaymentId = this.getUrlPaymentId();
    if (!selectedValues.length && urlPaymentId) {
      selectedValues = [urlPaymentId];
    }

    const newItems = Array.isArray(data)
      ? data
      : (Array.isArray(data?.data) ? data.data : []);

    if (!this.fullList) {
      this.fullList = [];
    }

    const mappedNewItems = newItems.map((item: any) => {
      const resolved = this.resolveUniqueItem(item);
      const value = resolved.value;
      const mergedItem = this.mergedList?.find((el: any) => el?.value?.toString() === value?.toString());

      const isChecked =
        mergedItem !== undefined
          ? mergedItem.checked
          : selectedValues.length > 0
            ? selectedValues.includes(value?.toString())
            : (this.isSearchActive ? this.isSelectAllSearchedResult : this.isSelectAll);

      return {
        text: resolved.text,
        value,
        checked: isChecked,
        type: this.columnConfig?.type || 'string',
        dateFormat: this.columnConfig?.dateFormat || this.DATE_FORMAT_MMDDYYYY,
        currencyFormat: this.columnConfig?.currencyFormat || 'USD',
        additionalText: resolved.additionalText,
        isBlanked: resolved.isBlanked
      };
    }).filter((item: any) => item.value !== undefined && item.value !== null);

    // Merge new items with existing ones, avoiding duplicates by 'value'
    const previousItems = this.isSearchActive ? this.searchedResults : this.fullList;
    const combined = [...previousItems, ...mappedNewItems];
    const uniqueByValue = new Map();
    combined.forEach(item => uniqueByValue.set(item.value, item));

    if (this.isSearchActive) {
      this.searchedResults = Array.from(uniqueByValue.values());
      if (this.fullList.length === 0) {
        this.fullList = [...this.searchedResults];
      }
    }
    else {
      this.fullList = Array.from(uniqueByValue.values());
    }
    this.filteredList = [...this.fullList];
    this.syncAndMergeCheckedItems(this.fullList, this.searchedResults, this.isSearchActive ? 'searched' : 'full');
  }

  onSearch(searchTerm: string) {
    const lowerSearch = searchTerm?.toString()?.trim().toLowerCase() || '';
    const isEmptySearch = !lowerSearch; // either null, empty string, or only spaces

    // Skip API call if trimmed value hasn't changed
    if (lowerSearch === this.previousSearchTerm) {
      return;
    }

    this.previousSearchTerm = lowerSearch; // Update previous value

    // Reset search-related state
    this.options.filterObject = {
      ...this.options.filterObject,
      [this.columnConfig?.uniqueFilter?.paramName[0]]: 1,
      page: 1,
      filter_limit: this.getFilterLimit(),
    };

    this.isScrolled = false;
    this.currentPage = 1;
    this.options.page = 1;
    this.searchText = lowerSearch;

    if (isEmptySearch) {
      // Reset: disable search mode
      this.isSearchActive = false;
      this.options.filterObject.filter_search = '';
      this.isSelectAllSearchedResult = false;
    } else {
      // Valid search string
      this.isSearchActive = true;
      this.isSelectAllSearchedResult = true;
      this.options.filterObject.filter_search = lowerSearch;
    }

    this.getColumnUniqueValues(); // Call only if search actually changed
  }


  onCancel(filterObject?: any) {
    this.cancelClicked.emit(filterObject);
  }

  getColumnUniqueValues() {
    let queryParams = Utility.getParamsFromObject(this.options?.filterObject || {});
    const filterKey = this.excelTypeFilterService.getParams(this.options.tableName, this.options?.filterObject.columnName, 1);
    // clearing the saved filter as if we send the payload for the same column which we are fetching unique values for we will get wrong result.
    this.savedFilterCloned = Utility.updateSavedFilter(this.savedFilterCloned, filterKey, null, 'delete');

    for (const key in this.savedFilterCloned) {
      if (this.savedFilterCloned.hasOwnProperty(key)) {
        const values = this.savedFilterCloned[key];
        if (values.length > 0) {
          queryParams += `&${key}=${values.join(',')}`;
        }
      }
    }
    const url = `${this.apiEndpoint}?${queryParams}`;
    this.httpService.get(url).subscribe((res) => {
      this.setPagination(res);
      this.initializeList(res);
    }, (err) => {
      this.msgService.showError(err?.error?.message);
    });
  }

  toggleSelectAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    if (this.isSearchActive) {
      this.searchedResults.forEach(item => item.checked = checked);
      this.isSelectAllSearchedResult = checked;
      if (!checked) {
        this.isSelectAll = false;
      }
      this.syncAndMergeCheckedItems(this.fullList, this.searchedResults, 'searched');
    }
    else {
      this.fullList.forEach(item => item.checked = checked);
      this.filteredList.forEach(item => item.checked = checked);
      this.isSelectAll = checked;
      this.isSelectAllSearchedResult = checked;
      this.syncAndMergeCheckedItems(this.fullList, this.searchedResults, 'full');
    }
  }

  areAllSelected(): boolean {
    return this.filteredList.length > 0 && this.filteredList?.every(item => item?.checked);
  }

  areAllSearchedSelected(): boolean {
    return this.searchedResults.length > 0 && this.searchedResults?.every(item => item?.checked);
  }

  onSave() {
    const filterKey = this.excelTypeFilterService.getParams(this.options.tableName, this.options?.filterObject?.columnName, 1);
    const hasExistingFilter = !!(this.savedFilterClonedBackup[filterKey] && this.savedFilterClonedBackup[filterKey][0]);

    // Select All on the full list means no column filter — same as initial page load
    if (!this.isSearchActive && (this.isSelectAll || this.areAllSelected())) {
      if (hasExistingFilter) {
        this.resetClicked.emit(this.options.filterObject.columnName);
      }
      this.onCancel();
      return;
    }

    const selected = this.isSearchActive
      ? this.searchedResults.filter(item => item.checked).map(item => item.value)
      : (this.mergedList?.length
        ? this.mergedList.map(item => item.value)
        : this.filteredList.filter(item => item.checked).map(item => item.value));

    const selectedValues = selected ?? [];
    const filteredList = this.filteredList?.map(item => item?.value) ?? [];

    if (!this.isSearchActive && this.areArraysEqual(selectedValues, filteredList) && this.areAllSelected() && !hasExistingFilter) {
      this.onCancel();
      return;
    }

    if (!this.isSearchActive && this.areArraysEqual(selectedValues, filteredList) && this.areAllSelected()) {
      this.resetClicked.emit(this.options.filterObject.columnName);
      this.onCancel();
      return;
    }

    if (selectedValues && hasExistingFilter && this.areArraysEqual(this.savedFilterClonedBackup[filterKey][0]?.split(','), selectedValues)) {
      this.onCancel();
      return;
    }

    this.options.filterObject.selectedValues = selected?.map((value: any) => String(value)).join(',');
    this.options.columnName = this.excelTypeFilterService.getParams(this.options?.tableName, this.options?.columnName, 1);

    this.onCancel(this.options);
  }

  isAnySelected(): boolean {
    if (this.isSearchActive) {
      return this.searchedResults.some(item => item.checked);
    }
    return this.filteredList.some(item => item.checked) || this.mergedList.some(item => item.checked);
  }

  reset() {
    this.isSearchActive = false;
    this.isSelectAll = true;
    this.isSelectAllSearchedResult = true;
    this.searchText = '';
    this.searchComponent.reset();
    this.options.filterObject.filter_search = '';
    this.filteredList.forEach(f => f.checked = true);
    this.resetClicked.emit(this.options.filterObject.columnName);

    this.searchedResults = [];
    this.fullList = [];
    this.mergedList = [];
    this.filteredList = [];


    this.options.filter_search = this.searchText = '';
    this.columnConfig = this.excelTypeFilterService.getColumnConfig(this.options.tableName, this.options.columnName);
    this.apiEndpoint = this.excelTypeFilterService.getApiEndpoint(this.options.tableName, this.options.historicData ? 1 : 0);
    if (this.apiEndpoint) {
      this.options.filterObject = {
        ...this.options.filterObject,
        [this.columnConfig?.uniqueFilter?.paramName[0]]: 1
      };
      this.options.filterObject.page = 1;
      this.options.filterObject.filter_limit = this.getFilterLimit();
      this.getColumnUniqueValues();
    }

    this.isSelectAll = true;

  }

  // on scroll get next set of list data
  onInfiniteScroll() {

    if (!this.isScrolled && (this.currentPage < this.totalPage)) {
      this.isScrolled = true;
      this.currentPage++;
      this.options.page = this.currentPage;
      this.options.filterObject.page = this.currentPage;

      let queryParams = Utility.getParamsFromObject(this.options?.filterObject || {});

      const filterKey = this.excelTypeFilterService.getParams(this.options.tableName, this.options?.filterObject.columnName, 1);
      this.savedFilterCloned = Utility.updateSavedFilter(this.savedFilterCloned, filterKey, null, 'delete');

      for (const key in this.savedFilterCloned) {
        if (this.savedFilterCloned.hasOwnProperty(key)) {
          const values = this.savedFilterCloned[key];
          if (values.length > 0) {
            queryParams += `&${key}=${values.join(',')}`;
          }
        }
      }

      const url = `${this.apiEndpoint}?${queryParams}`;
      this.httpService.get(url).subscribe((res) => {
        this.setPagination(res);
        this.initializeList(res);
        this.isScrolled = false;
      }, (err) => {
        this.isScrolled = false;
        this.msgService.showError(err?.error?.message);
      });
    }
  }

  private getFilterLimit(): number {
    return this.columnConfig?.filterLimit || 200;
  }

  private getUrlPaymentId(): string {
    if (this.options?.columnName !== 'payment_id') {
      return '';
    }
    const paymentId = this.options?.paymentId;
    if (paymentId === null || paymentId === undefined || paymentId === '') {
      return '';
    }
    return String(paymentId).trim();
  }

  private setPagination(res: any): void {
    this.currentPage = Number(res?.current_page ?? res?.pagination?.current_page ?? this.currentPage ?? 1);
    const total = Number(res?.total ?? res?.pagination?.total ?? 0);
    const perPage = Number(res?.per_page ?? res?.pagination?.per_page ?? res?.pagination?.size ?? 0);
    this.totalPage = perPage > 0 ? Math.ceil(total / perPage) : 1;
  }

  private resolveUniqueItem(item: any): { value: any; text: any; additionalText: any; isBlanked: boolean } {
    const identifier = this.columnConfig?.uniqueFilter?.identifier || 'id';
    const textField = this.columnConfig?.uniqueFilter?.textFieldName || 'code';
    const additionalField = this.columnConfig?.uniqueFilter?.additionalTextFieldName;

    if (item === null || item === undefined || typeof item !== 'object') {
      return {
        value: item,
        text: item,
        additionalText: undefined,
        isBlanked: item === '[BLANK]'
      };
    }

    let value = item[identifier];
    let text = item[textField];

    if (this.options?.columnName === 'payment_id') {
      value = value ?? item.payment_id ?? item.id;
      text = text ?? item.payment_id ?? value;
    }

    return {
      value,
      text,
      additionalText: additionalField ? item[additionalField] : undefined,
      isBlanked: text === '[BLANK]'
    };
  }

  areArraysEqual(arr1: any[], arr2: any[]): boolean {
    if (arr1.length !== arr2.length) return false;

    const toSortedStringArray = (arr: any[]) =>
      arr.map(String).sort();

    const sorted1 = toSortedStringArray(arr1);
    const sorted2 = toSortedStringArray(arr2);

    return sorted1.every((val, index) => val === sorted2[index]);
  }

  syncAndMergeCheckedItems(fullArray: any[], filteredArray: any[], source: 'full' | 'searched'): void {
    if (!Array.isArray(fullArray) || !Array.isArray(filteredArray)) return;

    if (source === 'full') {
      // Full list was changed so update filtered
      fullArray.forEach(fullItem => {
        const match = filteredArray.find(f => f.value === fullItem.value);
        if (match) {
          match.checked = fullItem.checked;
        }
      });
    } else if (source === 'searched') {
      // Searched list was changed so update full
      filteredArray.forEach(filteredItem => {
        const match = fullArray.find(f => f.value === filteredItem.value);
        if (match) {
          match.checked = filteredItem.checked;
        }
      });
    }

    // Return merged list with only checked items (deduplicated by `value`)
    const allItems = [...fullArray, ...filteredArray];
    const mergedMap = new Map();

    allItems.forEach(item => {
      if (item?.checked) {
        mergedMap.set(item.value, item);
      }
    });

    this.mergedList = Array.from(mergedMap.values());

    const allFullSelected = fullArray.length > 0 && fullArray.every(item => item?.checked);
    const allSearchedSelected = filteredArray.length > 0 && filteredArray.every(item => item?.checked);

    if (this.isSearchActive) {
      this.isSelectAllSearchedResult = allSearchedSelected;
      if (!allSearchedSelected) {
        this.isSelectAll = false;
      }
    } else {
      this.isSelectAll = allFullSelected;
      if (!allFullSelected) {
        this.isSelectAllSearchedResult = false;
      }
    }
  }



  ngOnDestroy(): void {}
}